import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Listbox } from "@headlessui/react";

// components
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
import HeaderLayout from "@surgia/ui/layouts/Header";
import { PurchaseOrderTableUntitled } from "../../containers/tables/PurchaseOrderTableUntitled";

// queries
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { adminQueries } from "@surgia/api/src/queryStore/admin";
import { ADMIN_PROTECTED_ROUTES } from "../../routes";

// icons

// constants
const ITEMS_PER_PAGE = 8;

// Tenant Select Component
interface Tenant {
  id: number;
  name: string;
}

interface TenantSelectProps {
  tenants: Tenant[];
  selectedTenant: string | undefined;
  onTenantChange: (tenantId: string | null) => void;
  isLoading?: boolean;
  className?: string;
  placeholder?: string;
}

const TenantSelect: React.FC<TenantSelectProps> = ({
  tenants,
  selectedTenant,
  onTenantChange,
  isLoading = false,
  className = "w-48 h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none",
  placeholder = "Select Tenant",
}) => {
  const selectedTenantObj =
    tenants.find((tenant) => tenant.id.toString() === selectedTenant) || null;

  return (
    <div className="flex flex-col gap-2 w-[250px]">
      <Listbox
        value={selectedTenantObj}
        onChange={(tenant) => {
          onTenantChange(tenant?.id.toString() || null);
        }}
      >
        <div className="relative">
          <Listbox.Button
            disabled={isLoading}
            className={`${className} ${isLoading ? "opacity-50 cursor-not-allowed" : ""} w-[250px]`}
          >
            <span className="block truncate text-left">
              {isLoading
                ? "Loading tenants..."
                : selectedTenantObj?.name || placeholder}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </Listbox.Button>

          <Listbox.Options className="absolute z-[1000] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
            {tenants.map((tenant) => (
              <Listbox.Option
                key={tenant.id}
                value={tenant}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                    >
                      {tenant.name || `Tenant ${tenant.id}`}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branch_id: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ID Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

// Main Purchase Orders Component
const PurchaseOrders: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [search] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    branch_id: "",
  });
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<
    string[]
  >([]);

  // Add tenant and branches state
  const [selectedTenant, setSelectedTenant] = useState<string | undefined>();
  const [selectedBranch, setSelectedBranch] = useState<string | null>();
  const [allUserBranches, setAllUserBranches] = useState<string>("");

  // Add sort tracking state
  const [currentSort, setCurrentSort] = useState<{
    column: string | null;
    direction: string | null;
  }>({ column: null, direction: null });

  // Fetch all tenants for admin
  const {
    data: tenantsResponse,
    isLoading: isTenantsLoading,
    isPending: isTenantsPending,
  } = useQuery({
    ...adminQueries.tenant.queries.tenants({
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
      size: 1000, // Get all tenants
    }),
    enabled: !!import.meta.env.VITE_PUBLIC_COMPONENT_ID,
  });

  // Get tenants from the API response
  const tenants = tenantsResponse?.data || [];

  // Determine which tenant to use for branches (selected tenant or first tenant)
  const tenantForBranches =
    selectedTenant ||
    (tenants.length > 0 ? tenants[0].id.toString() : undefined);

  // Get the selected tenant object to extract branches
  const selectedTenantObj = tenants.find(
    (tenant) => tenant.id.toString() === tenantForBranches
  );

  // Extract branch IDs from the selected tenant's branches
  const branchesIds = selectedTenantObj?.branch
    ? selectedTenantObj.branch.id.toString()
    : selectedTenantObj?.branches?.map((branch: any) => branch.id).join(",") ||
      "";

  // Set allUserBranches when tenant is loaded
  useEffect(() => {
    if (branchesIds) {
      setAllUserBranches(branchesIds);
    }
  }, [branchesIds]);

  // Set default tenant if none selected
  useEffect(() => {
    if (tenants.length > 0 && !selectedTenant) {
      setSelectedTenant(tenants[0].id.toString());
    }
  }, [tenants, selectedTenant]);

  // Track sort changes
  useEffect(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) {
      const [column, direction] = sortParam.split(":");
      const newSort = { column, direction };

      // Only log if the sort has actually changed
      if (
        currentSort.column !== column ||
        currentSort.direction !== direction
      ) {
        setCurrentSort(newSort);
      }
    }
  }, [searchParams]);

  // Fetch purchase orders data
  const { data: purchaseOrdersResponse, isLoading } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getPurchaseOrdersList({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenantForBranches || "", // Use the determined tenant
      branchesIds: branchesIds || "null", // Use extracted branch IDs
      sort: (() => {
        const sortParam = searchParams.get("sort");
        if (sortParam) {
          const [column, direction] = sortParam.split(":");
          // Map "ascending" to "asc" and "descending" to "desc"
          const mappedDirection = direction === "ascending" ? "asc" : "desc";
          return `${column},${mappedDirection}`;
        }
        // Default sort: id descending
        return "id,desc";
      })(),
      page: currentPage,
      size: ITEMS_PER_PAGE,
    }),
    enabled: !!(
      import.meta.env.VITE_PUBLIC_COMPONENT_ID &&
      selectedTenant &&
      !isTenantsPending
    ),

    select: (data) => {
      if ("errorCode" in data) {
        return {
          data: [],
          headers: {},
        };
      }
      if (Array.isArray(data.data)) {
        const transformed = data.data.map((po: any) => ({
          id: po.id,
          orderedBy: po.ordered_by,
          branch: po.branch?.name || "N/A",
          totalQuantity: po.total_quantity,
          "orderSummary.total": po.order_summary?.total || 0,
          createdAt: po.created_at,
          status: po.status,
          branchId: po.branch?.id || null,
          numberOfPurchaseRequests: po.number_of_purchase_requests,
        }));
        return {
          data: transformed,
          headers: data.headers,
        };
      }
      return {
        data: [],
        headers: {},
      };
    },
  });

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status, filters.id, selectedBranch, selectedTenant]);

  const handleViewPODetails = (poId: string, branchId: string) => {
    console.log("View PO details:", poId, branchId);
    // Navigate to the Purchase Order Details View page using the determined tenant
    navigate(
      ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_PURCHASE_ORDER_DETAILS.replace(
        ":id",
        tenantForBranches || ""
      ).replace(":poId", poId)
    );
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="">
      <HeaderLayout
        title="Purchase Orders Management"
        subtitle="Monitor and manage purchase orders across tenants"
        additionalStyles="justify-between"
        showBranchesDropDown={false}
        branches={[]}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allUserBranches}
        showAllBranches={true}
        isBranchesLoading={false}
      >
        <div className="flex gap-2">
          {/* Tenant Selector */}
          <div className="flex items-center gap-2">
            <TenantSelect
              tenants={tenants}
              selectedTenant={selectedTenant}
              onTenantChange={(tenantId) =>
                setSelectedTenant(tenantId || undefined)
              }
              isLoading={isTenantsLoading}
            />
          </div>
        </div>
      </HeaderLayout>

      {/* Purchase Orders Table Section using Untitled UI */}
      <PurchaseOrderTableUntitled
        data={purchaseOrdersResponse?.data || []}
        loading={isLoading}
        currentPage={currentPage}
        totalPages={Math.ceil(
          (purchaseOrdersResponse?.headers?.["x-total-count"] || 0) /
            ITEMS_PER_PAGE
        )}
        onPageChange={setCurrentPage}
        showBranchColumn={false}
        selectedPurchaseOrders={selectedPurchaseOrders}
        setSelectedPurchaseOrders={setSelectedPurchaseOrders}
        status={status}
        setStatus={setStatus}
        onViewDetails={handleViewPODetails}
        branches={[]}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allUserBranches}
        showBranchesDropDown={false}
        showAllBranches={false}
        isBranchesLoading={false}
        totalCount={purchaseOrdersResponse?.headers?.["x-total-count"] || 0}
      />

      {/* Footer with selection info and actions */}
      {status === "SUBMITTED" && selectedPurchaseOrders?.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 w-full bg-white px-6 py-2 border-t border-gray-200 z-50">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <span className="text-sm">
              Process Purchase Orders
              <span className="text-sm text-gray-600 ml-1">
                {selectedPurchaseOrders?.length || 0} Selected
              </span>
            </span>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setSelectedPurchaseOrders([])}
                disabled={selectedPurchaseOrders?.length === 0}
              >
                Dismiss
              </Button>
              <Button
                variant="primary"
                onClick={() => alert("Process selected purchase orders")}
                disabled={selectedPurchaseOrders?.length === 0}
              >
                Process Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}
    </div>
  );
};

export default PurchaseOrders;
